import {
  AdminExtendedAccount,
  InviteAccountUserParams,
} from '@admin-api/index';
import { Component, Inject } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface IInviteUserDialogData {
  account_id: string;
  account_type?: AdminExtendedAccount.TypeEnum;
}

const ACCT_ID_PATTERN = /^acct_/;

@Component({
  selector: 'invite-user-dialog',
  templateUrl: './invite-user-dialog.component.html',
})
export class InviteUserDialogComponent {
  form: UntypedFormGroup;
  roleOptions: InviteAccountUserParams.RoleEnum[];

  constructor(
    public matDialogRef: MatDialogRef<InviteUserDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: IInviteUserDialogData,
    private _formBuilder: UntypedFormBuilder,
  ) {
    this.roleOptions = this.rolesForAccountType(data.account_type);

    // account_id is shown read-only (disabled), so its validators wouldn't
    // run anyway — we validate it explicitly in submit() instead.
    this.form = this._formBuilder.group({
      account_id: [{ value: data.account_id, disabled: true }],
      email: ['', [Validators.required, Validators.email]],
      role: [null, Validators.required],
    });
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    // The disabled account_id control bypasses its validators, so guard here.
    if (!this.data.account_id || !ACCT_ID_PATTERN.test(this.data.account_id)) {
      return;
    }

    const { email, role } = this.form.getRawValue();
    const payload: InviteAccountUserParams = {
      account_id: this.data.account_id,
      email,
      role,
    };
    this.matDialogRef.close(payload);
  }

  cancel(): void {
    this.matDialogRef.close();
  }

  private rolesForAccountType(
    type: AdminExtendedAccount.TypeEnum | undefined,
  ): InviteAccountUserParams.RoleEnum[] {
    const all = Object.values(InviteAccountUserParams.RoleEnum);
    switch (type) {
      case AdminExtendedAccount.TypeEnum.MERCHANT:
        return all.filter((r) => r.startsWith('merchant_'));
      case AdminExtendedAccount.TypeEnum.RESELLER:
        return all.filter((r) => r.startsWith('reseller_'));
      case AdminExtendedAccount.TypeEnum.PARTNER:
        return all.filter(
          (r) => !r.startsWith('merchant_') && !r.startsWith('reseller_'),
        );
      default:
        // Unknown / not provided — fall back to all roles, backend will
        // reject mismatches with a 400 (showing in the snackbar).
        return all;
    }
  }
}
